import React, { useMemo } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import { useModalStore } from '@/store';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import ModelPriceTier from './ModelPriceTier';

export default function ModelChatIntro({ model, company, onSuggestionPress }) {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const openModal = useModalStore(state => state.actions.openModal);
  const styles = useMemo(() => createStyles(colors, isXS), [colors, isXS]);
  const starters = Array.isArray(model?.conversationStarters) ? model.conversationStarters.slice(0, 4) : [];
  const logo = model?.logoPath || company?.logoPath;

  return (
    <View style={styles.container}>
      <View style={styles.identity}>
        <View style={styles.logo}>
          {logo ? <Image source={{ uri: logo }} style={styles.logoImage} contentFit="cover" accessibilityLabel={`${model.name} logo`} />
            : <Icon name="sparkles" size={28} color={colors.text_color_secondary} />}
        </View>
        {!!company?.name && <Text size="xs" color="secondary" weight="medium" style={styles.company}>{company.name}</Text>}
        <Text accessibilityRole="header" weight="bold" style={styles.name}>{model?.name}</Text>
        {!!(model?.subtitle || model?.description) && <Text size="n" color="secondary" style={styles.description}>{model.subtitle || model.description}</Text>}
        <View style={styles.meta}>
          <ModelPriceTier model={model} />
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={`Details about ${model?.name}`}
            onPress={() => openModal('ModelDetailsModal', { modelId: model.id })}
            style={({ hovered, pressed }) => [styles.details, (hovered || pressed) && styles.hover]}
          >
            <Icon name="circle-info" size={13} color={colors.text_color_secondary} />
            <Text size="xs" color="secondary" weight="medium">Model details</Text>
          </Pressable>
        </View>
      </View>
      {!!starters.length && (
        <View style={styles.suggestions}>
          <Text size="xs" color="secondary" style={styles.suggestionHeading}>A few ideas to get started</Text>
          <View style={styles.grid}>
            {starters.map((starter, index) => (
              <Pressable
                key={`${index}-${starter}`}
                accessibilityRole="button"
                accessibilityLabel={starter}
                onPress={() => onSuggestionPress?.(starter)}
                style={({ hovered, pressed }) => [styles.suggestion, (hovered || pressed) && styles.hover]}
              >
                <Text size="s" style={styles.suggestionText}>{starter}</Text>
                <Icon name="arrow-up-right" size={12} color={colors.text_color_tertiary} />
              </Pressable>
            ))}
          </View>
        </View>
      )}
    </View>
  );
}

const createStyles = (colors, isXS) => StyleSheet.create({
  container: { width: '100%', gap: isXS ? 24 : 32 },
  identity: { alignItems: 'center', gap: 8 },
  logo: { width: isXS ? 56 : 68, height: isXS ? 56 : 68, borderRadius: isXS ? 16 : 20, overflow: 'hidden', alignItems: 'center', justifyContent: 'center', backgroundColor: colors.solid_color_secondary, marginBottom: 6 },
  logoImage: { width: '100%', height: '100%' },
  company: { letterSpacing: 0.3 },
  name: { fontSize: isXS ? 26 : 34, lineHeight: isXS ? 32 : 40, letterSpacing: -0.9, textAlign: 'center' },
  description: { maxWidth: 580, textAlign: 'center', lineHeight: 23 },
  meta: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 2 },
  details: { minHeight: 32, flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 10, borderRadius: 16 },
  suggestions: { gap: 12 },
  suggestionHeading: { textAlign: 'center' },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  suggestion: { width: isXS ? '100%' : '48%', flexGrow: 1, flexDirection: 'row', alignItems: 'center', gap: 14, minHeight: 58, paddingHorizontal: 16, paddingVertical: 14, borderRadius: 15, borderWidth: 1, borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_primary, ...(Platform.OS === 'web' ? { transition: 'background-color 160ms ease' } : {}) },
  suggestionText: { flex: 1, lineHeight: 20 },
  hover: { backgroundColor: colors.solid_color_secondary },
});
